
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import Layout from "@/components/Layout";
import type { User } from "@supabase/supabase-js";

const PRODUCT_OPTIONS = [
  { value: "tablets", label: "Custom Tablets" },
  { value: "pcs", label: "Custom PCs" },
  { value: "desktops", label: "Custom Desktops" },
  { value: "business", label: "Business Solutions" },
  { value: "other", label: "Other" },
];

interface ContactRow {
  id: number;
  name: string | null;
  "Email Address": string | null;
  "Phone Number": number | null;
  "Company Name": string | null;
  created_at: string | null;
  product_interest?: string | null;
  message?: string | null;
}

type SortKey = "created_at" | "name" | "Email Address" | "Company Name" | "Phone Number";
type SortDir = "asc" | "desc";

const AdminContacts = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filter, setFilter] = useState<string>("");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchContacts();
    }
  }, [sortKey, sortDir, filter, isAdmin]);

  async function checkAuth() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/auth');
        return;
      }

      setUser(session.user);

      // Check if user has admin role
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .single();

      if (error || !data) {
        toast({
          title: "Access Denied",
          description: "You do not have admin privileges.",
          variant: "destructive"
        });
        navigate('/');
        return;
      }

      setIsAdmin(true);
    } catch (err) {
      navigate('/auth');
    } finally {
      setAuthChecking(false);
    }
  }

  async function fetchContacts() {
    setLoading(true);
    try {
      // Fetch all columns, including the new ones
      const { data, error } = await supabase
        .from("TRUECORE GADGETS")
        .select("id, name, \"Email Address\", \"Phone Number\", \"Company Name\", created_at, product_interest, message")
        .order(sortKey, { ascending: sortDir === "asc" });
      
      if (error) {
        toast({
          title: "Error",
          description: "Failed to load contact submissions. Please try again.",
          variant: "destructive"
        });
        setContacts([]);
      } else {
        // Filter on product_interest if desired
        let filteredData = (data || []);
        if (filter) {
          filteredData = filteredData.filter((item: any) => item.product_interest === filter);
        }
        setContacts(filteredData);
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading contacts.",
        variant: "destructive"
      });
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/auth');
  }

  if (authChecking) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <Layout>
      <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold">Contact Submissions</h1>
        <Button variant="outline" onClick={handleSignOut}>
          Sign Out
        </Button>
      </div>
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <select
          className="border border-border rounded px-4 py-2 min-w-[200px]"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        >
          <option value="">All Product Interests</option>
          {PRODUCT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <Button variant="secondary" onClick={() => { setSortKey("created_at"); setSortDir("desc"); setFilter(""); }}>
          Reset Filters
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead onClick={() => handleSort("created_at")} className="cursor-pointer">Submitted {sortKey === "created_at" ? (sortDir === "asc" ? "↑" : "↓") : ""}</TableHead>
              <TableHead onClick={() => handleSort("name")} className="cursor-pointer">Name {sortKey === "name" ? (sortDir === "asc" ? "↑" : "↓") : ""}</TableHead>
              <TableHead onClick={() => handleSort("Email Address")} className="cursor-pointer">Email {sortKey === "Email Address" ? (sortDir === "asc" ? "↑" : "↓") : ""}</TableHead>
              <TableHead onClick={() => handleSort("Phone Number")} className="cursor-pointer">Phone {sortKey === "Phone Number" ? (sortDir === "asc" ? "↑" : "↓") : ""}</TableHead>
              <TableHead onClick={() => handleSort("Company Name")} className="cursor-pointer">Company {sortKey === "Company Name" ? (sortDir === "asc" ? "↑" : "↓") : ""}</TableHead>
              <TableHead>Product Interest</TableHead>
              <TableHead>Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">No submissions found.</TableCell>
              </TableRow>
            ) : (
              contacts.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</TableCell>
                  <TableCell>{c.name || "-"}</TableCell>
                  <TableCell>{c["Email Address"] || "-"}</TableCell>
                  <TableCell>{c["Phone Number"] || "-"}</TableCell>
                  <TableCell>{c["Company Name"] || "-"}</TableCell>
                  <TableCell>{c.product_interest || "-"}</TableCell>
                  <TableCell>{c.message || "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
    </Layout>
  );
};

export default AdminContacts;
